/**
 * Generate Media Types
 *
 * Type definitions for media generation functionality.
 */

export type MediaType = 'image' | 'video';
export type AspectRatio = '9:16' | '16:9' | '1:1';
export type QualityPreset = 'fast' | 'standard' | 'high' | 'premium';
export type VideoSpeedTier = 'standard' | 'premium' | 'elite';

/**
 * Video length options. MiniMax H3 only accepts frame counts of the form
 * 17n+5, and its cost scales SUPERLINEARLY with length (measured warm on a
 * 5090, v14 worker: 73f = 60s, 124f = 132s, 175f = 313s — exponent ~2.5).
 * That is why the ladder stops at 7s: 10s would be ~13 min and 15s ~32 min.
 */
export type VideoDurationSeconds = 3 | 5 | 7;

export interface VideoDurationOption {
  seconds: VideoDurationSeconds;
  frames: number;
  steps: number;
  /** Rough warm-path wait shown in the UI. */
  waitLabel: string;
  coins: number;
}

export const VIDEO_DURATION_OPTIONS: VideoDurationOption[] = [
  { seconds: 3, frames: 73, steps: 5, waitLabel: '~1 min', coins: 60 },
  { seconds: 5, frames: 124, steps: 6, waitLabel: '~2-3 min', coins: 100 },
  // 7s (175 frames) measures 313s on the GPU — past Vercel's 300s function
  // ceiling, so the result lands with nobody left to save it and the user is
  // charged for a video they never receive. Re-enable once generations are
  // finalized outside the request (reconciler/webhook), not before.
  // { seconds: 7, frames: 175, steps: 6, waitLabel: '~5 min', coins: 200 },
];

export function videoDurationOption(
  seconds: VideoDurationSeconds,
): VideoDurationOption {
  return (
    VIDEO_DURATION_OPTIONS.find((o) => o.seconds === seconds) ??
    VIDEO_DURATION_OPTIONS[1]!
  );
}

export interface GenerationConfig {
  characterId: string;
  prompt: string;
  negativePrompt: string;
  mediaType: MediaType;
  aspectRatio: AspectRatio;
  quality: QualityPreset;
}

export interface Generation {
  id: string;
  userId: string;
  characterId: string;
  config: GenerationConfig;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  resultUrl?: string;
  thumbnailUrl?: string;
  tokenCost: number;
  createdAt: Date;
  completedAt?: Date;
}

export interface CharacterOption {
  id: string;
  name: string;
  slug: string;
  avatarUrl: string;
  style: 'realistic' | 'anime' | 'furry';
  gender?: 'female' | 'male' | 'trans';
  isUserCreated: boolean;
}

export interface GenerateMediaState {
  selectedCharacterId: string | null;
  prompt: string;
  negativePrompt: string;
  mediaType: MediaType;
  aspectRatio: AspectRatio;
  quality: QualityPreset;
  videoDurationSeconds: VideoDurationSeconds;
  isGenerating: boolean;
  showAdvanced: boolean;
}

export const DEFAULT_GENERATE_STATE: GenerateMediaState = {
  selectedCharacterId: null,
  prompt: '',
  negativePrompt: '',
  mediaType: 'image',
  aspectRatio: '9:16',
  quality: 'standard',
  videoDurationSeconds: 5,
  isGenerating: false,
  showAdvanced: false,
};

// Token costs per media type and quality.
//
// ⚠ DISPLAY-ONLY FALLBACK. The authoritative price is whatever
// /admin/coin-pricing has set in the `coin_prices` table — every charge goes
// through getActionPrice()/getActionPriceAdmin() (see wallet-actions.ts).
//
// These numbers exist solely so the client-side cost preview in
// generate-media-form.tsx has something to render before a server roundtrip
// resolves the live price. They must stay in sync with the matching
// DEFAULT_COIN_PRICES keys (image_generation, image_generation_high,
// image_generation_premium, video_generation, video_generation_premium,
// video_generation_elite) for the preview to match the actual charge.
export const TOKEN_COSTS: Record<MediaType, Record<QualityPreset, number>> = {
  image: {
    fast: 25, // images have no fast tier in the UI; mirror standard for type completeness
    standard: 25,
    high: 35,
    premium: 50,
  },
  video: {
    fast: 60,
    standard: 100,
    high: 200,
    premium: 300,
  },
};

// Aspect ratio display names
export const ASPECT_RATIO_LABELS: Record<AspectRatio, string> = {
  '9:16': 'Portrait (9:16)',
  '16:9': 'Landscape (16:9)',
  '1:1': 'Square (1:1)',
};

// Quality preset descriptions (used for images)
export const QUALITY_DESCRIPTIONS: Record<QualityPreset, string> = {
  fast: 'Fastest generation',
  standard: 'Fast generation, good quality',
  high: 'Slower, better details',
  premium: 'Best quality, longest wait',
};

// Video quality descriptions (speed-focused)
export const VIDEO_QUALITY_DESCRIPTIONS: Record<QualityPreset, string> = {
  fast: '~1 min — 3s clip',
  standard: '~2-3 min — 5s clip',
  // Legacy GPU tiers (hidden in the video selector since MiniMax H3 — one
  // endpoint produces identical output regardless of these labels).
  high: 'H100 GPU — 2-3 min',
  premium: 'H200 GPU — 1-2 min',
};

// Video speed tiers - GPU and pricing config
// gpuPatterns is an ordered fallback list: try first GPU, then second, etc.
export const VIDEO_SPEED_TIERS: Record<
  VideoSpeedTier,
  {
    label: string;
    description: string;
    cost: number;
    gpu: string;
    gpuPatterns: string[];
    estimatedTime: string;
  }
> = {
  standard: {
    label: 'Standard',
    description: 'RTX 5090 GPU',
    cost: 100,
    gpu: 'RTX 5090',
    gpuPatterns: ['5090', 'RTX 6000 Ada', 'H100'], // 5090 → 6000 Ada → H100
    estimatedTime: '3-5 min',
  },
  premium: {
    label: 'Premium',
    description: 'H100 GPU',
    cost: 200,
    gpu: 'H100',
    gpuPatterns: ['H100', 'H200'], // H100 → H200 fallback
    estimatedTime: '2-3 min',
  },
  elite: {
    label: 'Elite',
    description: 'H200 GPU',
    cost: 300,
    gpu: 'H200',
    gpuPatterns: ['H200', 'B200'], // H200 first (cheaper), B200 fallback
    estimatedTime: '1-2 min',
  },
};
